"use client"

import { useTranslation } from "react-i18next"
import { useEffect, useRef, useState } from "react"
import {
  useVerifyAuthCodeMutation,
  useCreateVerifyEmailMutation,
  useGetCountryListQuery,
} from "@/hooks/api/useAccountQuery"
import useGetPwdIsValid from "@/app/signup/hooks/useGetPwdIsValid/useGetPwdIsValid"
import useGetConfirmPwdIsValid from "@/app/signup/hooks/useGetConfirmPwdIsValid/useGetConfirmPwdIsValid"
import { useCreatePlayerMutation, useJoinPlayerMutation } from "@/hooks/api/usePlayerQuery"
import { useCreateCoachMutation, useJoinCoachMutation } from "@/hooks/api/useCoachQuery"

import "./page.scss"
import TextField from "@/components/form/textField/TextField"
import Selector from "@/components/form/selector/Selector"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import Checkbox from "@/components/form/checkbox/Checkbox"
import { Link, MenuItem } from "@mui/material"
import BasicButton from "@/components/button/BasicButton"
import { Country, VerifyAuthCodeResponse } from "@/types/account"
import { createAccountStore } from "@/stores/account"
import { AccountTypeKey } from "@/constants/account"
import DatePicker from "@/components/form/datePicker/DatePicker"
import classNames from "classnames"
import { isNil } from "lodash"
import moment from "moment"
import { MainFooted } from "@/types/player"
import * as policies from "../../policy"
import PolicyDialog from "@/app/signup/PolicyDialog"
import { useAuth } from "@/providers/AuthProvider"
import useRouter from "@/hooks/router/useRoute"
import NumericStepper from "@/components/form/numericStepper/NumericStepper"
import keycloakInstance from "@/providers/keycloak"
import { createGlobalStore } from "@/stores/global"
import { getTokenPayload } from "@/utils/token"

// 가입 화면은 팀(종목) 컨텍스트 이전이라 축구 포지션 코드를 기본 제공한다.
const SIGNUP_POSITION_CODES = ["CF", "WF", "CAM", "CM", "CDM", "CB", "FB", "GK"]

export default function SignUp({ authCode }: { authCode: string }) {
  const { t, i18n } = useTranslation("")
  const { authenticated, token } = useAuth()
  const router = useRouter()
  const { setSelectedIdfTeam } = createAccountStore((state) => ({
    setSelectedIdfTeam: state.setSelectedIdfTeam,
  }))
  const { setSnackBar } = createGlobalStore((store) => ({
    setSnackBar: store.setSnackBar,
  }))
  const { mutateAsync: verifyAuthCodeMutation } = useVerifyAuthCodeMutation()
  const { mutateAsync: createVerifyEmailMutation } = useCreateVerifyEmailMutation()
  const { data: countryList } = useGetCountryListQuery()

  const [idfTeam, setIdfTeam] = useState(0)
  const [account, setAccount] = useState<VerifyAuthCodeResponse | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [pwd, setPwd] = useState<string | undefined>()
  const [confirmPwd, setConfirmPwd] = useState<string | undefined>()
  const isValidPwd = useGetPwdIsValid(pwd)
  const isValidConfirmPwd = useGetConfirmPwdIsValid(pwd, confirmPwd)
  const [gender, setGender] = useState<string>("")
  const [foot, setFoot] = useState<string>("")
  const [name, setName] = useState<string>("")
  const [birth, setBirth] = useState<Date | null>(null)
  const [height, setHeight] = useState<number | undefined>()
  const [weight, setWeight] = useState<number | undefined>()
  const [position, setPosition] = useState<string>("")
  const [terms, setTerms] = useState<string>("")
  const [policy, setPolicy] = useState<string>("")
  const [termsDialog, setTermsDialog] = useState(false)
  const [policyDialog, setPolicyDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const signupNewPlayer = useCreatePlayerMutation()
  const joinPlayer = useJoinPlayerMutation()
  const signupNewCoach = useCreateCoachMutation()
  const joinCoach = useJoinCoachMutation()

  const currentUserEmail = token ? getTokenPayload(token)?.email : null

  // API 중복 호출 방지용 ref
  const hasFetchedRef = useRef(false)

  const ableSignUp = () => {
    let able =
      !isNil(pwd) &&
      isValidPwd &&
      !isNil(confirmPwd) &&
      isValidConfirmPwd &&
      name.length > 0 &&
      !isNil(birth) &&
      !isNaN(birth.getTime()) &&
      gender.length > 0 &&
      agreed

    if (account?.type === AccountTypeKey.Player) {
      able = able && !isNil(height) && !isNil(weight) && position.length > 0 && foot.length > 0
    }

    return (account?.joined && agreed) || able
  }

  const openTermsDialog = (e: React.MouseEvent) => {
    e.stopPropagation()
    setTermsDialog(true)
  }
  const openPolicyDialog = (e: React.MouseEvent) => {
    e.stopPropagation()
    setPolicyDialog(true)
  }

  const onCheckboxChange = () => {
    setAgreed((prev) => !prev)
  }

  const handleSubmit = async () => {
    if (!account || isSubmitting) return

    setIsSubmitting(true)
    setSelectedIdfTeam(idfTeam)

    try {
      const isPlayer = account.type === AccountTypeKey.Player
      const isJoin = account.joined

      // Join 플로우: 기존 계정 팀 추가
      if (isJoin) {
        if (isPlayer) {
          await joinPlayer.mutateAsync({ idfPlayer: account.idfAccount, payload: { authCode } })
        } else {
          await joinCoach.mutateAsync({ idfCoach: account.idfAccount, payload: { authCode } })
        }

        setSnackBar({ open: true, message: t("messages.successAdd"), severity: "success" })
        // Snackbar가 보일 시간을 준 후 홈으로 리다이렉트 (페이지 전체 새로고침으로 토큰도 자동 갱신됨)
        await new Promise((resolve) => setTimeout(resolve, 1200))
        window.location.href = "/"
        return
      }

      // SignUp 플로우: 새 계정 생성
      const basePayload = {
        idfTeam,
        email: account.email,
        country: selectedCountry?.code ?? "",
        password: pwd as string,
        name,
        birthday: moment(birth as Date).format("YYYY-MM-DD"),
        gender,
        authCode,
      }

      if (isPlayer) {
        await signupNewPlayer.mutateAsync({
          ...basePayload,
          nickname: name,
          height: height as number,
          weight: weight as number,
          foot: MainFooted[foot as keyof typeof MainFooted],
          position,
        })
      } else {
        await signupNewCoach.mutateAsync(basePayload)
      }

      // 회원가입 성공 후 로그아웃하고 새 계정으로 로그인
      setSnackBar({ open: true, message: t("messages.successAdd"), severity: "success" })

      // Snackbar가 보일 시간을 준 후 로그아웃 → 로그인 페이지로 리다이렉트
      await new Promise((resolve) => setTimeout(resolve, 1200))

      // 로그아웃 후 바로 로그인 페이지로
      await keycloakInstance.logout({ redirectUri: window.location.origin })
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    // 이미 API 호출했으면 스킵
    if (hasFetchedRef.current) return

    // authCode 없으면 검증 불필요
    if (!authCode) return

    const verifyAccess = async (payload: VerifyAuthCodeResponse & { authCode: string }) => {
      const { joined, authCode, email } = payload

      // joined: false면 확인 불필요
      if (!joined) {
        return true
      }

      // joined: true면 현재 로그인되어 있어야 하고 이메일이 일치해야 함
      if (!authenticated) {
        return false
      }

      // 로그인된 이메일과 payload 이메일 비교
      if (currentUserEmail !== email) {
        return false
      }

      // verifyEmailMutation 통과 확인
      const { response } = await createVerifyEmailMutation({
        email: email as string,
        authCode,
      })

      return response?.verify ?? false
    }

    ;(async () => {
      hasFetchedRef.current = true

      try {
        const data = await verifyAuthCodeMutation(authCode)
        const response = data.response
        if (response) {
          const ableAccess = await verifyAccess({ authCode, ...response })
          if (!ableAccess) {
            // 접근 권한 없음 - 홈으로 리다이렉트
            setSnackBar({ open: true, message: t("messages.403"), severity: "error" })
            await new Promise((resolve) => setTimeout(resolve, 2000))
            router?.push("/")
            return
          }
          setAccount(response)
          setIdfTeam(response.idfTeam)
        }
      } catch (e) {
        await new Promise((resolve) => setTimeout(resolve, 2000))
        router?.push("/")
      }
    })()
  }, [
    authCode,
    verifyAuthCodeMutation,
    authenticated,
    createVerifyEmailMutation,
    router,
    currentUserEmail,
    t,
    setSnackBar,
  ])

  // account.country로 초기 국가 설정
  useEffect(() => {
    if (account?.country && countryList) {
      const initialCountry = countryList.find((c) => c.code === account.country)
      setSelectedCountry(initialCountry || null)
    }
  }, [account?.country, countryList])

  useEffect(() => {
    if (account) {
      // 영어 또는 한글로만 제공
      if (i18n.language !== "ko") {
        i18n.changeLanguage("en")
      }
      const languagePolicies = policies.default[i18n.language as keyof typeof policies.default]
      setTerms(languagePolicies.terms)
      setPolicy(
        account?.type === AccountTypeKey.Coach
          ? languagePolicies.privacyPolicyCoach
          : languagePolicies.privacyPolicyPlayer,
      )
    }
  }, [account])

  const content = () => {
    if (!account) return null
    if (account.joined)
      return (
        <>
          <div className="title">
            <span className="title-main">
              {t(account?.type === AccountTypeKey.Coach ? "coach" : "player")} {t("signUp")}
            </span>
            <span className="title-sub">{t("inviteTeam", { nameTeam: account?.nameTeam })}</span>
          </div>
          <div className="footer">
            <div className="agreement">
              <Checkbox size="medium" checked={agreed} onCheckboxChange={onCheckboxChange} />
              <span onClick={() => setAgreed((prev) => !prev)}>
                {i18n.language === "ko" ? (
                  <>
                    <Link component="button" onClick={openTermsDialog}>
                      이용약관
                    </Link>
                    <span className="footer-label">과 </span>
                    <Link component="button" onClick={openPolicyDialog}>
                      개인 정보 정책
                    </Link>
                    <span className="footer-label">에 동의합니다.</span>
                  </>
                ) : (
                  <>
                    <span className="footer-label">I agree to </span>
                    <Link component="button" onClick={openTermsDialog}>
                      Terms
                    </Link>
                    <span className="footer-label"> and </span>
                    <Link component="button" onClick={openPolicyDialog}>
                      Privacy Policy
                    </Link>
                  </>
                )}
              </span>
            </div>
            <BasicButton
              className="submit"
              type="primary-contained"
              size="md"
              disabled={!ableSignUp() || isSubmitting}
              onClick={handleSubmit}>
              {t("button.signUp")}
            </BasicButton>
          </div>
        </>
      )
    return (
      <>
        <div className="title">
          <span className="title-main">
            {t(account?.type === AccountTypeKey.Coach ? "coach" : "player")} {t("signUp")}
          </span>
          <span className="title-sub">{t("inviteTeam", { nameTeam: account?.nameTeam })}</span>
        </div>
        <div className="form">
          <TextField className="email" label={t("email")} value={account?.email || ""} disabled />
          <div className="form-section">
            <div className="password">
              <TextField
                type="password"
                label={t("password")}
                placeholder={t("password")}
                value={pwd || ""}
                onChange={(e) => setPwd(e.target.value)}
                error={!isValidPwd}
              />
              <span className={classNames("hint", { isNotValid: !isValidPwd })}>
                {t("messages.hintPassword")}
              </span>
            </div>
            <div className="confirm-password">
              <TextField
                type="password"
                label={t("confirmPassword")}
                placeholder={t("confirmPassword")}
                value={confirmPwd || ""}
                onChange={(e) => setConfirmPwd(e.target.value)}
                error={!isValidConfirmPwd}
              />
              {!isValidConfirmPwd && <span>{t("rules.passwordIncorrect")}</span>}
            </div>
          </div>
          <div className="form-section">
            <TextField
              label={t("name")}
              placeholder={t("name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 50 }}
            />
            <Selector
              value={selectedCountry}
              options={countryList || []}
              labelKey={i18n.language === "ko" ? "nameKr" : "name"}
              isOptionEqualToValue={(option: Country, value: Country) => option.code === value.code}
              renderOption={(props, option: Country) => {
                const { key, ...optionProps } = props

                return (
                  <MenuItem key={key} value={option.code} {...optionProps}>
                    {i18n.language === "ko" ? option.nameKr : option.name}
                  </MenuItem>
                )
              }}
              onChange={(_: any, value: Country) => {
                setSelectedCountry(value)
              }}
              label={t("nationality")}
            />
          </div>
          <div className="form-section">
            <div className="date">
              <span className="label">{t("birth")}</span>
              <DatePicker
                value={birth}
                placeholder="0000-00-00"
                defaultValueNull={true}
                maxDate={new Date()}
                onChange={(e) => {
                  if (e) {
                    setBirth(e)
                  }
                }}
              />
            </div>
            <div className="gender">
              <span className="label">{t("gender")}</span>
              <RadioGroup
                items={[
                  { label: t("male"), value: "M" },
                  { label: t("female"), value: "F" },
                ]}
                direction="row"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              />
            </div>
          </div>
          {account?.type === AccountTypeKey.Player && (
            <>
              <div className="form-section">
                <div className="health">
                  <span className="health-label">{t("height")} (cm)</span>
                  <NumericStepper
                    value={height}
                    onChange={setHeight}
                    min={0}
                    max={999}
                    allowNegative={false}
                    decimalScale={0}
                  />
                </div>
                <div className="health">
                  <span className="health-label">{t("weight")} (kg)</span>
                  <NumericStepper
                    value={weight}
                    onChange={setWeight}
                    min={0}
                    max={999}
                    allowNegative={false}
                    decimalScale={0}
                  />
                </div>
              </div>
              <div className="form-section">
                <Selector
                  options={SIGNUP_POSITION_CODES}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props

                    return (
                      <MenuItem key={key} value={option} {...optionProps}>
                        {option}
                      </MenuItem>
                    )
                  }}
                  onChange={(_: any, value: string) => setPosition(value)}
                  label={t("position")}
                  placeholder={t("select")}
                />
                <div className="gender">
                  <span className="gender-label">{t("foot")}</span>
                  <RadioGroup
                    items={[
                      { label: t("left"), value: "Left" },
                      { label: t("right"), value: "Right" },
                      { label: t("all"), value: "All" },
                    ]}
                    direction="row"
                    value={foot}
                    onChange={(e) => setFoot(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="footer">
          <div className="agreement">
            <Checkbox size="medium" checked={agreed} onCheckboxChange={onCheckboxChange} />
            <span onClick={() => setAgreed((prev) => !prev)}>
              {i18n.language === "ko" ? (
                <>
                  <Link component="button" onClick={openTermsDialog}>
                    이용약관
                  </Link>
                  <span className="footer-label">과 </span>
                  <Link component="button" onClick={openPolicyDialog}>
                    개인 정보 정책
                  </Link>
                  <span className="footer-label">에 동의합니다.</span>
                </>
              ) : (
                <>
                  <span className="footer-label">I agree to </span>
                  <Link component="button" onClick={openTermsDialog}>
                    Terms
                  </Link>
                  <span className="footer-label"> and </span>
                  <Link component="button" onClick={openPolicyDialog}>
                    Privacy Policy
                  </Link>
                </>
              )}
            </span>
          </div>
          <BasicButton
            className="submit"
            type="primary-contained"
            size="md"
            disabled={!ableSignUp() || isSubmitting}
            onClick={handleSubmit}>
            {t("button.signUp")}
          </BasicButton>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="signup">{content()}</div>
      <PolicyDialog
        policy={terms}
        onClickAgree={() => setAgreed(true)}
        nameTeam={account?.nameTeam}
        open={termsDialog}
        onClose={() => setTermsDialog(false)}
      />
      <PolicyDialog
        policy={policy}
        onClickAgree={() => setAgreed(true)}
        nameTeam={account?.nameTeam}
        open={policyDialog}
        onClose={() => setPolicyDialog(false)}
      />
    </>
  )
}
